/**
 * AbholbereitMailPanel — manueller Start des Flows
 * auftragsverfolgung-abholbereit-mail (26.08.2026, Testphase).
 *
 * Bewusst im Einstellungen-Tab: v1 hat KEINEN Schedule, Testmodus ist hart an
 * (alle Mails an matthias.frank@anstoss24.de), Allowlist ist Pflicht.
 */
import { useState } from 'react';
import { Mail, PlayCircle, RefreshCw } from 'lucide-react';
import { api } from '@libero/core/services/api';
import FlowResultPanel from '@libero/core/components/flow/FlowResultPanel';

export default function AbholbereitMailPanel() {
  const [auftragsNummern, setAuftragsNummern] = useState('');
  const [dryRun, setDryRun] = useState(true);
  const [executionId, setExecutionId] = useState<string | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);

  async function handleStart() {
    setStartError(null);
    setIsStarting(true);
    try {
      const res = await api.flows.executeById('auftragsverfolgung-abholbereit-mail', {
        auftragsNummern,
        dryRun,
      });
      if (res.success && res.data?.executionId) setExecutionId(res.data.executionId);
      else setStartError('Start fehlgeschlagen');
    } catch (err: any) {
      setStartError(err?.response?.data?.error?.message ?? err.message ?? 'Start fehlgeschlagen');
    } finally {
      setIsStarting(false);
    }
  }

  return (
    <div className="bg-white rounded-lg border border-[#DAD9D9] p-4 mt-6">
      <h3 className="text-base font-semibold text-[#1A1A1A] flex items-center gap-2 mb-1">
        <Mail className="w-4 h-4" /> Abholbereit-Kundenmail
      </h3>
      <p className="text-xs text-gray-500 mb-3">
        Sendet die Abholbereit-/Erinnerungs-Mail für Pakete, die laut DHL zur Abholung
        bereitliegen. <strong className="text-[#1A1A1A]">Diese Mails gehen an echte Kunden.</strong>{' '}
        Läuft automatisch um 9:00 und 15:00 — ein manueller Start ist nur für Einzelfälle nötig.
        Auftragsnummern leer lassen = alle passenden Aufträge. Nicht dabei: Amazon- und
        Kaufland-Bestellungen sowie Pakete, die schon vor der Live-Schaltung in der Station lagen.
      </p>
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col text-xs text-gray-600 gap-1">
          Auftragsnummern (optional, komma-separiert)
          <input
            type="text"
            value={auftragsNummern}
            onChange={(e) => setAuftragsNummern(e.target.value)}
            placeholder="leer = alle fälligen"
            className="border border-[#DAD9D9] rounded px-2 py-1.5 text-sm w-72 font-mono"
          />
        </label>
        <label className="flex items-center gap-2 text-sm text-[#1A1A1A] cursor-pointer select-none pb-1.5">
          <input
            type="checkbox"
            checked={dryRun}
            onChange={(e) => setDryRun(e.target.checked)}
            className="w-4 h-4 accent-[#1A1A1A]"
          />
          Dry-Run (nur anzeigen, nichts schreiben/senden)
        </label>
        <button
          onClick={handleStart}
          disabled={isStarting}
          className="flex items-center gap-2 px-4 py-2 bg-[#E9FF57] text-[#1A1A1A] font-semibold rounded-lg hover:bg-[#d4ea4d] disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
        >
          {isStarting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
          {isStarting
            ? 'Startet…'
            : dryRun
              ? 'Dry-Run starten'
              : auftragsNummern.trim().length > 0
                ? 'Mails senden (nur diese Aufträge)'
                : 'Mails senden (alle fälligen)'}
        </button>
      </div>
      {startError && <p className="text-xs text-red-600 mt-2">{startError}</p>}
      {executionId && (
        <div className="mt-4">
          <FlowResultPanel
            flowId="auftragsverfolgung-abholbereit-mail"
            executionId={executionId}
            onNewRun={() => setExecutionId(null)}
          />
        </div>
      )}
    </div>
  );
}
